import { DateTimeSelection } from 'accounts/components/multiselect-date-time-range-picker/multiselect-date-time-range-picker';

interface DateTimeValidationState {
  showDateValidationErrors: boolean;
  showTimeValidationErrors: boolean;
  isValidationToastOpen: boolean;
}

interface DateTimeValidationConfig {
  minSelections: number;
  isHandledByAnExternalProvider?: boolean;
}

interface DateTimeValidationResult {
  shouldShowToast: boolean;
  validationState: DateTimeValidationState;
}

/**
 * Determines the appropriate validation state based on current selections and selected date
 */
export function getDateTimeValidationState(
  selectedSelections: DateTimeSelection[],
  // `undefined` reaches here when the picker reports no date at all. Note that
  // `hasSelectedDate` below compares against `null` only, so it reads `undefined`
  // as a picked date — recorded in the followups doc rather than changed here,
  // since it decides which validation toast the homeowner sees.
  selectedDate: string | null | undefined,
  config: DateTimeValidationConfig,
): DateTimeValidationResult {
  const { minSelections, isHandledByAnExternalProvider = false } = config;
  const minRequired = isHandledByAnExternalProvider ? 1 : minSelections;

  // If we have enough selections, no validation needed
  if (selectedSelections.length >= minRequired) {
    return {
      shouldShowToast: false,
      validationState: {
        showDateValidationErrors: false,
        showTimeValidationErrors: false,
        isValidationToastOpen: false,
      },
    };
  }

  // Determine what validation to show
  const hasSelectedDate = selectedDate !== null;
  const hasTimeForSelectedDate =
    selectedDate && selectedSelections.some((s) => s.date === selectedDate);

  let showDateValidationErrors = false;
  let showTimeValidationErrors = false;

  if (hasSelectedDate && !hasTimeForSelectedDate) {
    // Date selected but no time - show time validation
    showTimeValidationErrors = true;
  } else if (selectedSelections.length === 0) {
    // No selections at all - show date validation
    showDateValidationErrors = true;
  } else {
    // Some selections but not enough - show date validation
    showDateValidationErrors = true;
  }

  return {
    shouldShowToast: true,
    validationState: {
      showDateValidationErrors,
      showTimeValidationErrors,
      isValidationToastOpen: true,
    },
  };
}
